import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { intelligenceApi } from '../lib/adapter';
import type { AILead, AIChannel, SalesStage } from '../types/contracts';
import { IntelligenceShell } from '../components/IntelligenceShell';
import { LeadScore } from '../components/LeadScore';
import { LeadStage } from '../components/LeadStage';
import { EmptyState, ErrorState, Skeleton } from '../components/states';
import { CHANNEL_ICON, timeAgo } from '../lib/format';

const STAGE_FILTERS: { key: string; label: string; match: (l: AILead) => boolean }[] = [
  { key: 'all', label: 'الكل', match: () => true },
  { key: 'new', label: 'جديد', match: (l) => l.stage === 'new' },
  { key: 'contacted', label: 'تم التواصل', match: (l) => l.stage === 'contacted' },
  { key: 'engaged', label: 'متفاعل', match: (l) => l.stage === 'qualified' },
  { key: 'demo', label: 'تجريبي', match: (l) => l.stage === 'demo' },
  { key: 'trial', label: 'فترة تجربة', match: (l) => l.stage === 'trial' },
  { key: 'hot', label: 'ساخن', match: (l) => l.temperature === 'hot' },
  { key: 'won', label: 'تم الإغلاق', match: (l) => l.stage === 'won' },
  { key: 'lost', label: 'مفقود', match: (l) => l.stage === 'lost' },
];

const SOURCES: (AIChannel | 'all')[] = ['all', 'whatsapp', 'website', 'facebook', 'instagram', 'manual', 'referral'];

export default function LeadsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [leads, setLeads] = useState<AILead[]>([]);
  const [stage, setStage] = useState('all');
  const [source, setSource] = useState<AIChannel | 'all'>('all');
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    const r = await intelligenceApi.listLeads();
    setLoading(false);
    if (!r.ok) { setError('message' in r ? r.message : 'حدث خطأ ما'); return; }
    setLeads(r.data);
  };
  useEffect(() => { void load(); }, []);

  const filtered = useMemo(() => {
    const f = STAGE_FILTERS.find((x) => x.key === stage) ?? STAGE_FILTERS[0];
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (!f.match(l)) return false;
      if (source !== 'all' && l.source !== source) return false;
      if (q && ![l.name, l.business, l.phone].join(' ').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [leads, stage, source, query]);

  return (
    <IntelligenceShell onSearch={setQuery} searchValue={query}>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 overflow-x-auto">
          {STAGE_FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setStage(f.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap ${stage === f.key ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {f.label}
            </button>
          ))}
        </div>
        <select value={source} onChange={(e) => setSource(e.target.value as AIChannel | 'all')}
          className="ms-auto rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700">
          {SOURCES.map((s) => <option key={s} value={s}>{s === 'all' ? 'كل المصادر' : ({ whatsapp: 'واتساب', website: 'الموقع', facebook: 'فيسبوك', instagram: 'إنستغرام', manual: 'يدوي', referral: 'إحالة' } as Record<string, string>)[s] ?? s}</option>)}
        </select>
      </div>

      {loading ? <Skeleton rows={6} /> : null}
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}
      {!loading && !error && filtered.length === 0 ? (
        <EmptyState title="لا يوجد عملاء محتملون" body="جرّب تغيير فلتر المرحلة أو المصدر أو كلمة البحث." />
      ) : null}
      {!loading && !error && filtered.length > 0 ? (
        <ul className="space-y-2">
          {filtered.map((l) => (
            <li key={l.id} className="rounded-2xl bg-white border border-gray-100 px-5 py-4 flex flex-wrap items-center gap-4 hover:border-teal-200 transition-colors">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-gray-900">{l.name}</p>
                  <p className="text-xs text-gray-500">{l.business}</p>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
                  <span className="flex items-center gap-1"><i className={CHANNEL_ICON[l.source]} /> {l.source}</span>
                  <span>آخر تفاعل: {timeAgo(l.last_interaction_at)}</span>
                  <span>الإجراء التالي: <b className="text-gray-700">{l.next_action}</b></span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <LeadScore score={l.score} temperature={l.temperature} />
                <LeadStage stage={l.stage as SalesStage} />
                <button type="button" onClick={() => navigate(`/intelligence/leads/${l.id}`)}
                  className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-[11px] font-bold hover:bg-gray-800">
                  فتح
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </IntelligenceShell>
  );
}
